import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Why isn't my antidepressant working?",
    answer:
      "An antidepressant may not work because the dose was too low, the trial was too short (most need 6 to 8 weeks at a therapeutic dose), the medication class was not right for your biology, or the diagnosis needs reassessment. Bipolar depression, untreated medical conditions, and substance use can all mimic treatment resistance. A psychiatrist can review whether your trials were adequate and whether the diagnosis is correct.",
  },
  {
    question:
      "How long should I give an antidepressant before deciding it isn't working?",
    answer:
      "Most antidepressants need 6 to 8 weeks at a therapeutic dose to show their full effect. Partial improvement at 4 weeks is common. If there is no improvement after 8 weeks at an adequate dose, or if side effects prevent reaching an adequate dose, a psychiatrist may recommend switching, augmenting, or trying a non-medication treatment.",
  },
  {
    question: "Can depression become resistant to medication over time?",
    answer:
      "Depression can become less responsive to a medication that once worked — this is sometimes called tachyphylaxis or 'poop-out.' It can also recur after a period of remission. If a medication that previously worked stops working, a psychiatrist may adjust the dose, switch to a different medication, or add a non-medication treatment like TMS or SAINT®.",
  },
  {
    question: "What are the signs of treatment-resistant depression?",
    answer:
      "The primary sign is persistent depressive symptoms after two or more adequate antidepressant trials. Other signs include partial response that plateaus, recurrent episodes despite medication, or side effects that prevent adequate dosing. A psychiatrist confirms the diagnosis by reviewing medication history, doses, durations, and screening for conditions that can mimic resistance.",
  },
];

export const pageWhyAntidepressantNotWorking: LearnPageData = {
  slug: "why-isnt-my-antidepressant-working",
  title: "Why Isn't My Antidepressant Working? | Neuro Wellness",
  description:
    "If your antidepressant isn't working, it may be dose, duration, the wrong class, or a diagnosis that needs reassessment. Learn what to do next.",
  canonical: "/learn/why-isnt-my-antidepressant-working",
  h1: "Why Isn't My Antidepressant Working?",
  breadcrumbLabel: "Why Isn't My Antidepressant Working?",
  crumbs: baseCrumbs("Why Isn't My Antidepressant Working?"),
  shortAnswer:
    "An antidepressant may not work because the dose was too low, the trial was too short (most need 6 to 8 weeks at a therapeutic dose), the medication class was not right for your biology, or the diagnosis needs reassessment. Bipolar depression, untreated medical conditions, and substance use can all mimic treatment resistance. A psychiatrist can review whether your trials were adequate and whether the diagnosis is correct.",
  keyTakeaways: [
    "Most antidepressants need 6 to 8 weeks at a therapeutic dose to show full effect.",
    "An inadequate trial (too low a dose or too short) is not the same as a failed trial.",
    "Bipolar depression, thyroid conditions, and substance use can mimic treatment resistance.",
    "After two adequate trials, a psychiatrist may recommend TMS, SAINT®, or SPRAVATO®.",
  ],

  sections: [
    {
      heading: "Common Reasons an Antidepressant Isn't Working",
      body: (
        <>
          {P(
            "The most common reason is an inadequate trial: the dose was too low or the medication was stopped before 6 to 8 weeks. Other reasons include the wrong medication class for your biology, an incomplete diagnosis (such as bipolar depression misdiagnosed as unipolar), untreated medical conditions like hypothyroidism, and substance use. A psychiatrist reviews each of these before concluding a medication has truly failed.",
          )}
        </>
      ),
    },
    {
      heading: "Was Your Trial Adequate?",
      body: (
        <>
          {P(
            "An adequate trial generally means taking an antidepressant at a therapeutic dose — not just a starting dose — consistently for 6 to 8 weeks. A medication stopped early because of side effects, or never increased beyond the starting dose, is usually considered an incomplete trial rather than a failed one. Reviewing pharmacy records with a psychiatrist is the first step.",
          )}
        </>
      ),
    },
    {
      heading: "Could the Diagnosis Be Different?",
      body: (
        <>
          {P(
            "Depressive episodes in bipolar disorder often respond poorly to antidepressants alone and can be mistaken for resistant unipolar depression. Untreated comorbidities — anxiety, PTSD, ADHD, substance use — and medical conditions like hypothyroidism or sleep apnea can also produce the appearance of resistance. A careful reassessment sometimes changes the diagnosis or reveals a correctable cause.",
          )}
        </>
      ),
    },
    {
      heading: "What to Do Next",
      body: (
        <>
          {P(
            "Ask your psychiatrist: Were my trials truly adequate? Should I try a different class or augmentation? Am I a candidate for TMS, SAINT®, or SPRAVATO®? What does my insurance cover?",
          )}
          {P(
            <>
              <a
                href="/depression-treatment-not-working"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See our full guide on when treatment isn't working
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Rush AJ, et al. STAR*D report. Am J Psychiatry. 2006.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/why-isnt-my-antidepressant-working#webpage`,
        name: "Why Isn't My Antidepressant Working?",
        url: `${SITE_URL}/learn/why-isnt-my-antidepressant-working`,
        description: "Why an antidepressant may not work and what to do next.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/why-isnt-my-antidepressant-working#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Why Isn't My Antidepressant Working?")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "What Is Treatment-Resistant Depression?",
      href: "/learn/what-is-treatment-resistant-depression",
      description: "Definition and options",
    },
    {
      title: "When Antidepressants Don't Work",
      href: "/learn/when-antidepressants-dont-work",
      description: "What to try next",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
